/**
 * DEMO · Idea 7 — Dos opciones con precio.
 *
 * Real (del modelo): qué muros hay y el área, largo y altura de cada uno (medición de ci + caja de la
 * pieza). De ejemplo: los conceptos de cada opción, las reglas de los complementarios (cada cuánto va un
 * castillo o una dala, cuántas caras se aplanan) y los precios.
 */
import { useMemo, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { DemoVisor3D, type Pintura } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import { cantidadDe, coincide, coloresDemo, crearContexto, num2, pesos, type Selector } from '../../components/demos/logicaDemo.js';
import datos from '../../components/demos/datos/dos-opciones.json';
import { LoQueFalta } from '../../components/demos/LoQueFalta.js';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'dos-opciones')!;

interface ConceptoOpcion {
  id: string;
  descripcion: string;
  unidad: string;
  base: 'area' | 'castillos' | 'dalas';
  factor?: number;
  separacion?: number;
  pu: number;
}
const OPCIONES = datos.opciones as { id: string; nombre: string; conceptos: ConceptoOpcion[] }[];
const ACTUAL = OPCIONES[0]!;
const ALTERNATIVA = OPCIONES[1]!;

const altoDe = (e: ElementoDemo) => (e.caja ? e.caja.max[1] - e.caja.min[1] : 0);

/** Cantidad de un concepto de la opción sobre los muros elegidos, con la regla de su JSON. */
function cantidadOpcion(c: ConceptoOpcion, muros: ElementoDemo[]): number {
  let s = 0;
  for (const e of muros) {
    const area = cantidadDe(e, 'area');
    const largo = cantidadDe(e, 'longitud');
    const alto = altoDe(e);
    if (c.base === 'area') s += area * (c.factor ?? 1);
    else if (c.base === 'castillos' && c.separacion) s += (Math.ceil(largo / c.separacion) + 1) * alto;
    else if (c.base === 'dalas' && c.separacion) s += Math.max(1, Math.ceil(alto / c.separacion)) * largo;
  }
  return s;
}

export function DemoDosOpciones() {
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [elegidos, setElegidos] = useState(false);
  const [comparar, setComparar] = useState(false);
  const colores = useMemo(() => coloresDemo(), []);

  const muros = useMemo(() => {
    if (!modelo) return [];
    const ctx = crearContexto(modelo.elementos);
    return modelo.elementos.filter((e) => coincide(e, datos.seleccion.selector as Selector, ctx));
  }, [modelo]);
  const clavesMuros = useMemo(() => new Set(muros.map((e) => e.clave)), [muros]);

  const filas = useMemo(() => {
    const ids = [...new Set([...ACTUAL.conceptos, ...ALTERNATIVA.conceptos].map((c) => c.id))];
    return ids.map((id) => {
      const a = ACTUAL.conceptos.find((c) => c.id === id);
      const b = ALTERNATIVA.conceptos.find((c) => c.id === id);
      const qa = a ? cantidadOpcion(a, muros) : 0;
      const qb = b ? cantidadOpcion(b, muros) : 0;
      const ia = a ? qa * a.pu : 0;
      const ib = b ? qb * b.pu : 0;
      const cambia = !a || !b || a.descripcion !== b.descripcion || Math.abs(ia - ib) >= 1;
      return { id, a, b, qa, qb, ia, ib, cambia };
    });
  }, [muros]);
  const totA = filas.reduce((s, f) => s + f.ia, 0);
  const totB = filas.reduce((s, f) => s + f.ib, 0);
  const delta = totB - totA;
  const areaTotal = muros.reduce((s, e) => s + cantidadDe(e, 'area'), 0);

  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        if (!elegidos) return {};
        return clavesMuros.has(clave) ? { color: colores.acento, opacidad: 1 } : { color: colores.neutro, opacidad: 0.1 };
      },
    [elegidos, clavesMuros, colores],
  );

  const celda = (c: ConceptoOpcion | undefined, q: number, imp: number) =>
    c ? (
      <td style={estiloTabla.td}>
        {c.descripcion}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
          {num2(q)} {c.unidad} × {pesos(c.pu)} = <b style={{ color: 'var(--text-primary)' }}>{pesos(imp)}</b>
        </div>
      </td>
    ) : (
      <td style={{ ...estiloTabla.td, color: 'var(--text-tertiary)' }}>No lleva</td>
    );

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.3fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button appearance={elegidos ? 'secondary' : 'primary'} disabled={!modelo} onClick={() => setElegidos(true)}>
              1 · Elegir {datos.seleccion.nombre.toLowerCase()}
            </Button>
            <Button appearance={comparar ? 'secondary' : 'primary'} disabled={!elegidos} onClick={() => setComparar(true)}>
              2 · Comparar con block 15
            </Button>
          </div>
          <DemoVisor3D
            fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
            onListo={(m) => setModelo(m.a ?? null)}
            pintar={pintar}
            alto={520}
          />
          {elegidos && (
            <div style={{ fontSize: 'var(--fs-300)', color: 'var(--text-secondary)' }}>
              {muros.length} muros elegidos · {num2(areaTotal)} m² medidos en el modelo
            </div>
          )}
        </div>
        <Panel titulo="Lado a lado">
          {!comparar ? (
            <div style={{ color: 'var(--text-secondary)' }}>
              Elige los muros y pide la comparación. Se recalculan el muro y todo lo que lo acompaña: castillos, dalas y aplanados.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <Cifra etiqueta="Opción actual" valor={pesos(totA)} />
                <Cifra etiqueta="Block 15" valor={pesos(totB)} />
                <Cifra
                  etiqueta="Diferencia"
                  valor={`${delta >= 0 ? '+' : '−'}${pesos(Math.abs(delta))}`}
                  color={delta < 0 ? 'var(--ok)' : 'var(--bad)'}
                  sub={totA ? `${delta < 0 ? 'ahorras' : 'cuesta'} ${Math.abs(Math.round((delta / totA) * 100))} %` : ''}
                />
              </div>
              <table style={estiloTabla.tabla}>
                <thead>
                  <tr>
                    <th style={estiloTabla.th}>{ACTUAL.nombre}</th>
                    <th style={estiloTabla.th}>{ALTERNATIVA.nombre}</th>
                    <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.id} style={{ background: f.cambia ? 'var(--warn-weak)' : undefined }}>
                      {celda(f.a, f.qa, f.ia)}
                      {celda(f.b, f.qb, f.ib)}
                      <td style={{ ...estiloTabla.num, fontWeight: 600, color: f.ib - f.ia < 0 ? 'var(--ok)' : f.ib - f.ia > 0 ? 'var(--bad)' : undefined }}>
                        {f.ib - f.ia >= 0 ? '+' : '−'}
                        {pesos(Math.abs(f.ib - f.ia))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...estiloTabla.td, fontWeight: 700 }}>{pesos(totA)}</td>
                    <td style={{ ...estiloTabla.td, fontWeight: 700 }}>{pesos(totB)}</td>
                    <td style={{ ...estiloTabla.num, fontWeight: 700 }}>
                      {delta >= 0 ? '+' : '−'}
                      {pesos(Math.abs(delta))}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <NotaEjemplo>
                En ámbar, lo que cambia. Todo lo que se deriva del muro (castillos, dalas, aplanados, sellador) es una propuesta para revisar.{' '}
                {datos.aviso}
              </NotaEjemplo>
            </>
          )}
        </Panel>
      </div>
      {comparar && (
        <LoQueFalta modelo={modelo} conceptosEnHoja={datos.complementariosDe} yaIncluidos={datos.complementosIncluidos} />
      )}
    </DemoMarco>
  );
}

function Cifra({ etiqueta, valor, sub, color }: { etiqueta: string; valor: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
      <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>{etiqueta}</div>
      <div style={{ fontSize: 'var(--fs-500)', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
      {sub && <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}
